import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { RootState } from '../app/store';
import { ConfirmationModelInterface } from '../models/confirmation';

export interface ConfirmationState {
  model: ConfirmationModelInterface;
  originalInitData: any | null;
}

const initialState: ConfirmationState = {
  model: {
    flights: [],
    checkedInPassengers: [],
    luggagePickup: '',
    isSingleFlight: true,
    anyBagsSelected: true,
    messages: {},
    excludeFromLocalStorage: [
      'bagDestinationMatch',
      'finalAirportDestinationISO',
      'finalAirportDestination',
    ],
    excludeFromUpdate: [
      'flights.passengers.parsedLuggageInfo',
      'flights.passengers.fqtvNumber',
      'flights.passengers.fqtvProgram',
    ],
    canCheckoutOnward: false,
    canCheckoutOutbound: true,
    finalBagDestinations: [],
    fullCkiProcess: true,
    route: 'ck_confirmation',
    routeEligibleForBagTags: false,
    paxesEligibleForBagTags: false,
    passengerClasses: ['ECONOMY_CLASS'],
    lowestAmount: null,
    upgradeAvailable: false,
    adokMessages: null,
    parsedAdokMessages: null,
    upgradeAllowed: false,
    showFinalBagsDestination: false,
    confirmPermission: false,
    supportedFqtv: [],
  },
  originalInitData: null,
};

export const confirmationReducer = createSlice({
  name: 'confirmation',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetConfirmationModel: (state) => {
      return initialState;
    },
    setOriginalInitData: (state, action: PayloadAction<any>) => {
      // Only set originalInitData once (if not already set)
      if (state.originalInitData === null) {
        state.originalInitData = action.payload;
      }
    },
    setConfirmationModel: (state, action) => {
      state.model = action.payload;
    },
    setConfirmationModelAttribute: (state, action: PayloadAction<{ key: string; value: any }>) => {
      const key = action.payload.key;
      const value = action.payload.value;
      Object.assign(state.model, { [key]: value });
    },
    changeFqtvNumber: (
      state,
      action: PayloadAction<{ passengerId: number; fqtvNumber: string }>
    ) => {
      const passengerId = action.payload.passengerId;
      const fqtvNumber = action.payload.fqtvNumber;
      state.model.flights.forEach((flight) => {
        let passenger = flight.passengers.find((x) => x.paxIdReference === passengerId);
        if (passenger) {
          passenger.fqtvNumber = fqtvNumber;
        }
      });
    },
    changeFqtvProgram: (
      state,
      action: PayloadAction<{
        passengerId: number;
        fqtvProgram: string;
      }>
    ) => {
      const passengerId = action.payload.passengerId;
      const fqtvProgram = action.payload.fqtvProgram;
      state.model.flights.forEach((flight) => {
        let passenger = flight.passengers.find((x) => x.paxIdReference === passengerId);
        if (passenger) {
          passenger.fqtvProgram = fqtvProgram;
        }
      });
    },
    setConfirmationPaxAttribute: (
      state,
      action: PayloadAction<{
        paxid: number;
        key: string;
        value: any;
      }>
    ) => {
      const paxid = action.payload.paxid;
      const key = action.payload.key;
      const value = action.payload.value;
      state.model.flights.forEach((flight) => {
        let pax = flight.passengers.find((x) => x.paxIdReference === paxid);
        if (pax) {
          _.set(pax, key, value);
        }
      });
      state.model.checkedInPassengers.forEach((pax) => {
        if (pax.paxIdReference === paxid) {
          _.set(pax, key, value);
        }
      });
    },
    changeSelectedNumberOfBagTags: (
      state,
      action: PayloadAction<{
        passengerId: number;
        flightIdReference: number;
        bagTags: number;
      }>
    ) => {
      const passengerId = action.payload.passengerId;
      const bagTags = action.payload.bagTags;

      state.model.flights.forEach((flight) => {
        let passenger = flight.passengers.find((x) => x.paxIdReference === passengerId);
        if (passenger) {
          passenger.parsedLuggageInfo.selectedNumberOfBagTags = bagTags;
          passenger.parsedLuggageInfo.bagTagNumberChanged = true;
        }
      });

      let showFinalBagsDestination = false;
      state.model.flights.forEach((flight) => {
        flight.passengers.forEach((pax) => {
          if (pax.parsedLuggageInfo.selectedNumberOfBagTags > 0) {
            showFinalBagsDestination = true;
          }
        });
      });
      state.model.showFinalBagsDestination = showFinalBagsDestination;
    },
    setBagTagAsSelected: (state, action: PayloadAction<{ index: number; value: boolean }>) => {
      if (state.model.finalBagDestinations) {
        state.model.finalBagDestinations[action.payload.index].selected = action.payload.value;
      }
    },
    baggageDropChanged: (state, action: PayloadAction<number>) => {
      let lastIdAllowed = action.payload;
      let model = state.model;
      let flightId = model.flights[0].flightIdReference;
      let firstFlightPaxes = model.flights[0].passengers;

      if (flightId > lastIdAllowed) {
        // Set the number of selected bag tags on each passenger to 0
        // This is because we treat flights that are not in scope
        // of the baggage drop down as having no available bags for
        // purchase
        model.checkedInPassengers.forEach((pax) => {
          pax.parsedLuggageInfo.selectedNumberOfBagTags = 0;
          pax.parsedLuggageInfo.numberOfBagTags = 0;
          pax.parsedLuggageInfo.disabled = true;
        });
      } else {
        // Set the number of selected bag tags on each passenger based
        // on outbound flight
        model.checkedInPassengers.forEach((pax) => {
          let firstFlightBagTags =
            firstFlightPaxes[pax.paxIdReference].parsedLuggageInfo.selectedNumberOfBagTags;
          pax.parsedLuggageInfo.disabled = false;
          pax.parsedLuggageInfo.selectedNumberOfBagTags = firstFlightBagTags;
        });
      }
    },
    resetMesages: (state) => {
      state.model.messages = null;
    },
  },
});

export const {
  setConfirmationModel,
  setOriginalInitData,
  setConfirmationModelAttribute,
  changeFqtvNumber,
  changeFqtvProgram,
  changeSelectedNumberOfBagTags,
  setBagTagAsSelected,
  baggageDropChanged,
  resetConfirmationModel,
  setConfirmationPaxAttribute,
  resetMesages,
} = confirmationReducer.actions;

export const selectConfirmationModel = (state: RootState) => {
  return state.confirmation.model;
};
export const selectOriginalInitData = (state: RootState) => {
  return state.confirmation.originalInitData;
};

export default confirmationReducer.reducer;
